import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep, isEmpty } from "lodash";
import {
  swatch,
  downloadIcon,
  colouredAddCircle,
} from "../../assets/components/svgs";
import InputField from "../shared/Fields/InputField";
import {
  getLanguageFileName,
  isValidLocale,
  getLanguageName,
  getGlobalBucketName,
  updateLocalization,
  updateTranslationObj,
} from "./Util";
import Clipboard from "../../assets/clipboard.svg";
import Dropzone from "../shared/DragAndDrop/Dropzone";
import { IconButton } from "../primitives/buttons/IconButton";
import BiButton from "../primitives/buttons/BiButton.primitive";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";
import PickList, { PICKLIST_TYPES } from "../shared/Fields/PickList";
import { AppConstants } from "../../constants/AppConstants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setToastData } from "../../app/toastReducer";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import Card from "../shared/cards/Card";
import { getGCPFile, listFiles } from "../../http/asset-management-services";
import { HTTP_STATUS } from "../../http/constants/http.status";
import UploadLanguageCard from "./UploadLanguageCard";
import { LanguageObject, defaultLanguageSchema } from "./Policies";
import { useLocalization } from "../../hooks/useLocalization";

/**
 * @parentId customization
 * @manager Language
 * @overview Manage the overall Localization of the portal.
 * <section>
 *   <p>
 *     The third tab under Customization is Language, which is used to manage the entire localization of the portal.
 *     Admin Users can manage language packs, add more languages for the portal to support, and add different locales.
 *   </p>
 *   <p>
 *     These are primarily JSON Files stored on the Google Cloud Platform. Their configuration, however, is maintained in the config.json file, which holds the filename and the language name.
 *   </p>
 *   <p>
 *     <b>Add Language Pack File</b><br/>
 *     Admin users can add a new language pack file by clicking on the <strong>Add New Language</strong> button, facilitating the process of adding a new language file.
 *     Upon entering the Language Name, Locale Name, and uploading the JSON file, the Save Button gets enabled. Clicking on it will initiate the save process.
 *     <br/><br/>
 *     <b>View Language Pack File</b><br/>
 *     Admin users can select language pack file options from the dropdown list. Clicking on a dropdown item populates the details of the file.
 *     <br/><br/>
 *     <b>Download Language Pack File</b><br/>
 *     After selecting a language pack, a download button is located at the bottom. Clicking on it will download the JSON File.
 *     <br/><br/>
 *     <b>Update Language Pack</b><br/>
 *     After selecting a language pack, Admin users can click on the Upload language file button. A system popup will become visible, asking the user to select the new file.
 *     Upon file selection, the Save button will become visible, and clicking on the button will initiate the save process.
 *     <br/><br/>
 *   </p>
 *   <p>
 *     <h4>Language Form Fields</h4>
 *     Create Language Pack will display the following fields with the specified validation constraints.
 *     <br/>
 *     <table>
 *       <tr>
 *         <th>Field Name</th>
 *         <th>Required</th>
 *         <th>Data Type</th>
 *         <th>Field Type</th>
 *         <th>Constraints</th>
 *         <th>Editable</th>
 *       </tr>
 *       <tr>
 *         <td>Language Name</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>Locale Name</td>
 *         <td>Yes</td>
 *         <td>String</td>
 *         <td>Textfield</td>
 *         <th>NA</th>
 *         <td>Yes</td>
 *       </tr>
 *       <tr>
 *         <td>File</td>
 *         <td>Yes</td>
 *         <td>JSON File</td>
 *         <td>Drag Drop or Button</td>
 *         <th>.json format</th>
 *         <td>Yes</td>
 *       </tr>
 *     </table>
 *     <br/>
 *   </p>
 * </section>
 * <section>
 *   <h3>Functionality</h3>
 *   <section>
 *     <h3>Create New Language</h3>
 *     <br>
 *     Once the Admin User clicks on Save after entering required details in Add New Language mode, clicking on the Save button calls the AMS-05 Create Signed URL.
 *     On success, it calls the Upload GCP File PUT Call and uploads the file in the bucket. After successful upload, the config.json file is updated with the new language file.
 *   </section>
 *   <section>
 *     <h3>Updating Language</h3>
 *     <br>
 *     Once the Admin User selects a language to update and uploads the language file, clicking on the Save button calls the AMS-05 Create Signed URL.
 *     On success, it calls the Upload GCP File PUT Call and updates the file in the bucket.
 *   </section>
 * </section>
 * <section>
 *   <h3>Failure Status Codes</h3>
 *   <p> This section describes the Language Status Code information. <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Error Code</th>
 *         <th>Error Message</th>
 *       </tr>
 *       <tr>
 *         <td>403</td>
 *         <td>AMS_PERMISSION_DENIED</td>
 *         <td>You do not have permission to view this page.</td>
 *       </tr>
 *       <tr>
 *         <td>500</td>
 *         <td>AMS_INTERNAL_ERROR</td>
 *         <td>Internal Server Error</td>
 *       </tr>
 *       <tr>
 *         <td>503</td>
 *         <td></td>
 *         <td>Service Unavailable</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 * <section>
 *   <h3>Dependent System settings, Platform services & Role Permission </h3>
 *   <p>This section describes the list of dependent system settings & platform services required for the functioning of the Language page.</p>
 *   <h4>System Settings</h4>
 *   <p>Table lists all the dependent system setting(s) defined in the configuration service with either global/organization scope.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Key</th>
 *       <th>Type</th>
 *       <th>Value</th>
 *       <th>Scope</th>
 *     </tr>
 *     <tr>
 *       <td>None</td>
 *       <td>None</td>
 *       <td>None</td>
 *       <td>None</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <h4>Platform Service(s) </h4>
 *   <p>Table lists all the dependent platform service(s) with specific version(s) for Language.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Service Name</th>
 *       <th>Version</th>
 *     </tr>
 *     <tr>
 *       <td>Asset Management Service</td>
 *       <td>1.3.0</td>
 *     </tr>
 *     <tr>
 *       <td>User Management Service</td>
 *       <td>1.2.2</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <h4>API Role Permission(s) </h4>
 *   <p>Table lists the required API role permissions for Language.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Feature</th>
 *       <th>API URL</th>
 *       <th>API Method</th>
 *       <th>API Permission</th>
 *       <th>Required</th>
 *     </tr>
 *     <tr>
 *       <td>Create Signed URL</td>
 *       <td>/signed-url</td>
 *       <td>POST</td>
 *       <td>asset-management-service.signed-url.create</td>
 *       <td>Yes</td>
 *     </tr>
 *     <tr>
 *       <td>List Files</td>
 *       <td>/bucket/listfiles</td>
 *       <td>POST</td>
 *       <td>asset-management-service.bucket.files</td>
 *       <td>Yes</td>
 *     </tr>
 *   </table>
 * </section>
 */

const Language = () => {
  const { t } = useTranslation();
  const { getRTLLayout } = useLocalization();
  const rtlClasses = getRTLLayout();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState<LanguageObject>(defaultLanguageSchema);

  const fileRef = useRef<any>(null);

  const userData = useAppSelector(
    (state: any) => state.userManagement.userProfileInfo
  );

  console.log(data);
  const getLanguageOptions = async () => {
    try {
      let payload = {
        folderPath: `${AppConstants.getLanguagesGCPPath()}`,
        expirationTime: 30,
        generateSignedUrl: true,
        bucketName: getGlobalBucketName(),
      };
      let response = await listFiles(payload, {
        page: AppConstants.DEFAULT_PAGE,
        size: AppConstants.MAXIMUM_PAGE_SIZE,
      });
      const { data: responseData, status } = response;
      if (HTTP_STATUS.isSuccess(status)) {
        if (responseData.data.length > 0) {
          {
            let displayNameArr = responseData.data
              .filter((files: any) => files.type === AppConstants.GCP_FILES)
              .map((obj: any) => ({
                ...obj,
                displayName:
                  obj.type === AppConstants.GCP_FILES
                    ? getLanguageName(obj)
                    : "",
              }));
            if (fileRef?.current?.value) {
              fileRef.current.value = "";
            }
            setData({
              ...data,
              createMode: false,
              file: null,
              localeName: "",
              languageName: "",
              currentOption: [],
              listOptions: displayNameArr,
            });
          }
        }
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
    }
  };

  async function initialize() {
    try {
      await getLanguageOptions();
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    initialize();
  }, []);

  const savePayload = async () => {
    function errorHandling(errorText: string) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: errorText,
          isToastActive: true,
          toastType: "error",
        })
      );
    }

    setIsLoading(true);

    updateLocalization(data.file, userData?.userId, data.languageName)
      .then(async (response) => {
        await updateTranslationObj(
          data.file,
          userData?.userId,
          data.languageName
        );
        dispatch(
          setToastData({
            toastMessage: t("T_LANGUAGE_UPDATED_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );

        await getLanguageOptions();
      })
      .catch((error) => {
        errorHandling(t("T_ERROR_SOMETHING_WRONG"));
      });
  };

  const saveChanges = async () => {
    if (data.createMode && !isValidLocale(data.localeName)) {
      setData({
        ...data,
        localeNameError: t(`T_VALID_LOCALE_FORMAT`).replace(
          "%s",
          data.localeName
        ),
      });
      return;
    }
    await savePayload();
  };

  const discardChanges = () => {
    if (fileRef?.current?.value) {
      fileRef.current.value = "";
    }

    setData(defaultLanguageSchema);
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <MenuButtonsPortal>
        <BiButton
          className={`tertiary ${rtlClasses.marginRight} border py-1.5`}
          type="button"
          onClick={discardChanges}
        >
          {t("T_CANCEL")}
        </BiButton>

        <BiButton
          className={"bg-primary py-1.5 text-white"}
          type="button"
          disabled={isEmpty(data.file)}
          onClick={saveChanges}
        >
          {t("T_SAVE")}
        </BiButton>
      </MenuButtonsPortal>
      <div className="p-4">
        <UploadLanguageCard
          title={t("T_LANGUAGE_PACKS")}
          setData={setData}
          ref={fileRef}
          data={data}
          addButtonTitle={t("T_LANGUAGE_ADD_BUTTON")}
          pickerTitle={t("T_LANGUAGE")}
          fileKey={`${AppConstants.getLanguagesGCPPath()}${AppConstants.ASSETS_LOCALISATION_FILE_PREFIX}${data.localeName}.${AppConstants.DATA_TYPE_JSON}`}
        ></UploadLanguageCard>
      </div>
    </LoaderContainer>
  );
};

export default Language;
